import { App, AppState, URLOpenListenerEvent } from "@capacitor/app";
import { AppWeb } from "@capacitor/app/dist/esm/web";
import { Device } from "@capacitor/device";
import { DeviceWeb } from "@capacitor/device/dist/esm/web";
import { Geolocation } from "@capacitor/geolocation";
import { GeolocationWeb } from "@capacitor/geolocation/dist/esm/web";
import { SplashScreen } from "@capacitor/splash-screen";
import { SplashScreenWeb } from "@capacitor/splash-screen/dist/esm/web";
import { ToastWeb } from "@capacitor/toast/dist/esm/web";
import { isCapacitor } from "./client";
import { Urls } from "./urls";
import { VisibilityState, VisibilityStates } from "./visibility";

/*
    See: https://github.com/bushfireio/ux/issues/96 and https://github.com/ionic-team/capacitor-plugins/issues/635

    tristan@Tristans-MacBook-Air ux % npm run build

    > ux@3.0.0 build
    > react-scripts build && cp -f build/service-worker.js build/sw.js

    Creating an optimized production build...
    Failed to compile.

    Failed to minify the bundle. Error: static/js/647.11ee551f.chunk.js from Terser plugin
    Unexpected token: punc (:) [static/js/647.11ee551f.chunk.js:3,9]
        at /Users/tristan/bf/ux/node_modules/react-scripts/scripts/build.js:188:23
        at finalCallback (/Users/tristan/bf/ux/node_modules/webpack/lib/Compiler.js:415:32)
        at /Users/tristan/bf/ux/node_modules/webpack/lib/Compiler.js:432:13
        at Hook.eval [as callAsync] (eval at create (/Users/tristan/bf/ux/node_modules/tapable/lib/HookCodeFactory.js:33:10), <anonymous>:24:1)
        at Hook.CALL_ASYNC_DELEGATE [as _callAsync] (/Users/tristan/bf/ux/node_modules/tapable/lib/Hook.js:18:14)
        at onCompiled (/Users/tristan/bf/ux/node_modules/webpack/lib/Compiler.js:430:21)
        at /Users/tristan/bf/ux/node_modules/webpack/lib/Compiler.js:1106:17
        at eval (eval at create (/Users/tristan/bf/ux/node_modules/tapable/lib/HookCodeFactory.js:33:10), <anonymous>:14:1)
    Read more here: https://cra.link/failed-to-minify
*/

export class CapacitorIntegration {
    static initialise() {
        if (!isCapacitor()) return;
        console.log("CapacitorIntegration::initialise");
        CapacitorIntegration.setupGeolocation();
        CapacitorIntegration.setupDeeplinks();
        CapacitorIntegration.initialiseResumeListener();

        // Solution to capacitor tree-shaking issue!
        // @capacitor/* where * refers to a plugin uses dynamic imports (let fish = import('./web').then(do stuff))
        // when the plugin is invoked, e.g. by calling Geolocation for the geoloc plugin.
        new SplashScreenWeb();
        new ToastWeb();

        console.log("CapacitorIntegration::initialise finished");
    }

    static finalise() {
        if (!isCapacitor()) return;

        // Let some loady things settle down
        setTimeout(() => {
            SplashScreen.hide();
        }, 500);
    }

    private static setupGeolocation() {
        new GeolocationWeb();

        // Override 'navigator.geolocation' to use Capacitor's implementation, rather than the Safari Wk
        // So dirty but so amazing!
        (navigator as any).__defineGetter__("geolocation", () => Geolocation);
    }

    private static setupDeeplinks() {
        new AppWeb();
        App.addListener("appUrlOpen", CapacitorIntegration.onDeepLink);

        App.addListener("backButton", () => {
            if (window.location.pathname !== "/") {
                // If the user is not on the home page, we can go back
                window.history.back();
            }
        });
    }

    // Handle deep links - urls to be opened by the app
    private static onDeepLink(event: URLOpenListenerEvent) {
        console.log("onDeepLink", event);

        // Ensure the event, url and context are valid
        if (!event || !event.url) return;

        const requestedUrl = event.url;

        Urls.redirectToPathNormalise(requestedUrl, null);
    }

    static async isAppleSignInSupported() {
        if (!isCapacitor()) return false;

        // Fetch from capacitor
        const appleDeviceInfo = await CapacitorIntegration.deviceOsInfo();
        const version = appleDeviceInfo.osVersion;

        // Try and determine the integer version
        if (version && version.includes(".")) {
            const splits = version.split(".");
            if (splits && splits.length >= 2) {
                const versionInt = parseInt(splits[0]);

                // SIWA is supported on IOS 13+
                if (versionInt >= 13) {
                    console.log("CapacitorIntegration::isAppleSignInSupported true", version);
                    return true;
                }
            }
        }

        console.log("CapacitorIntegration::isAppleSignInSupported false", version);

        return false;
    }

    static async deviceOsInfo() {
        new DeviceWeb();
        return await Device.getInfo();
    }

    static initialiseResumeListener() {
        if (!isCapacitor()) return;
        App.addListener("appStateChange", CapacitorIntegration.onAppStateChanged);
    }

    static onAppStateChanged(state: AppState) {
        VisibilityStates.updateVisibilityState(
            state.isActive ? VisibilityState.Visible : VisibilityState.Hidden
        );
    }

    // static setupMigrationEvents(context: IAppContext) {
    //     window.addEventListener("message", function (event: MessageEvent<any>) {
    //         // Ensure the event is trusted
    //         if (event.isTrusted === false) {
    //             return;
    //         }

    //         // Ensure the event is from an expected origin
    //         if (
    //             !event.origin.startsWith("android-app://testy.bushfire.io") &&
    //             !event.origin.startsWith("android-app://qa.bushfire.io") &&
    //             !event.origin.startsWith("android-app://bushfire.io")
    //         ) {
    //             // console.log(
    //             //     "CapacitorIntegration::setupMigrationEvents unexpected origin",
    //             //     event.origin
    //             // );
    //             return;
    //         }

    //         // TODO Ensure the event is from an expected origin
    //         console.log("CapacitorIntegration::setupMigrationEvents", event);

    //         // get the port then use it for communication.
    //         const port = event.ports[0];
    //         if (typeof port === "undefined") {
    //             return;
    //         }

    //         // Save the port for later
    //         window.port = port;

    //         // Receive upcoming messages on this port.
    //         port.onmessage = function (event) {
    //             console.log("CapacitorIntegration::setupMigrationEvents incoming message", event);
    //         };

    //         const migraationReady: NativeAppEvent = {
    //             event: NativeAppEventType.MigrationReady,
    //             isLoggedIn: context.isLoggedIn,
    //             areNotificationsEnabled: context.notificationsShouldBeActive,
    //             token: context.token || null,
    //             deviceId: context.currentDevice().deviceId,
    //         };

    //         // Send the migration ready event
    //         port.postMessage(JSON.stringify(migraationReady));
    //     });
    // }

    // static async fetchMigrationState(): Promise<NativeAppEvent | undefined> {
    //     if (!isAndroidApp()) return;

    //     // Manually fetch keys from migration state
    //     await Preferences.configure({
    //         group: "migration",
    //     });

    //     const event = await Preferences.get({ key: "event" });
    //     console.log("CapacitorIntegration::fetchMigrationState", event.value);

    //     if (event.value && event.value === NativeAppEventType.MigrationReady) {
    //         const isLoggedIn = (await Preferences.get({ key: "isLoggedIn" })).value;
    //         const areNotificationsEnabled = (
    //             await Preferences.get({ key: "areNotificationsEnabled" })
    //         ).value;
    //         const token = (await Preferences.get({ key: "token" })).value;
    //         const deviceId = (await Preferences.get({ key: "deviceId" })).value;

    //         const migrationState: NativeAppEvent = {
    //             event: NativeAppEventType.MigrationReady,
    //             isLoggedIn: isLoggedIn ? JSON.parse(isLoggedIn) : null,
    //             areNotificationsEnabled: areNotificationsEnabled
    //                 ? JSON.parse(areNotificationsEnabled)
    //                 : null,
    //             token: token,
    //             deviceId: deviceId,
    //         };

    //         console.log(
    //             "CapacitorIntegration::fetchMigrationState",
    //             JSON.stringify(migrationState)
    //         );

    //         return migrationState;
    //     }
    // }

    // static async completeMigration(): Promise<undefined> {
    //     if (!isCapacitor()) return;

    //     // Manually fetch keys from migration state
    //     await Preferences.configure({
    //         group: "migration",
    //     });

    //     await Preferences.set({
    //         key: "event",
    //         value: NativeAppEventType.MigrationComplete,
    //     });

    //     console.log("CapacitorIntegration::completeMigration");
    // }
}

export enum NativeAppEventType {
    NativeReady = "NativeReady",
    MigrationReady = "MigrationReady",
    MigrationComplete = "MigrationComplete",
    SwitchToTwa = "SwitchToTwa",
    SwitchToCapacitor = "SwitchToCapacitor",
}

export type NativeAppEvent = {
    event: NativeAppEventType;
    isLoggedIn: boolean | null;
    areNotificationsEnabled: boolean | null;
    token: string | null;
    deviceId: string | null;
};
